import * as React from 'react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import {
  useMutationSetManualTenantBlocked,
  useQueryManualTenants,
  ManualTenantSummary,
} from '@/modules/shared/query-client/useQueryManualTenants'

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('pl-PL')
}

const computeStatus = (
  t: ManualTenantSummary,
): { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' } => {
  if (t.permanentlyBlocked) return { label: 'ZABLOKOWANA', tone: 'bad' }
  const now = Date.now()
  if (t.suspendedFrom && t.suspendedTo) {
    const from = new Date(t.suspendedFrom).getTime()
    const to = new Date(t.suspendedTo).getTime()
    if (from <= now && now <= to) return { label: 'ZAWIESZONA', tone: 'warn' }
  }
  if (!t.subscriptionExpirationDate) return { label: 'BRAK SUB.', tone: 'neutral' }
  const expMs = new Date(t.subscriptionExpirationDate).getTime()
  if (expMs <= now) return { label: 'WYGASŁA', tone: 'bad' }
  const daysLeft = (expMs - now) / 86400_000
  if (!t.paid) return { label: 'NIEOPŁACONA', tone: 'warn' }
  if (daysLeft < 7) return { label: 'WKRÓTCE WYGASA', tone: 'warn' }
  return { label: 'AKTYWNA', tone: 'ok' }
}

const toneClasses: Record<'ok' | 'warn' | 'bad' | 'neutral', string> = {
  ok: 'tw-bg-green-100 tw-text-green-800',
  warn: 'tw-bg-amber-100 tw-text-amber-800',
  bad: 'tw-bg-red-100 tw-text-red-800',
  neutral: 'tw-bg-gray-100 tw-text-gray-700',
}

export const ManualBillingTab: React.FC = () => {
  const { data, isLoading, error, refetch } = useQueryManualTenants()
  const navigate = useNavigate()
  const setBlocked = useMutationSetManualTenantBlocked()
  const confirm = useConfirm()
  const [filter, setFilter] = useState('')

  const rows = useMemo<ManualTenantSummary[]>(() => {
    if (!data) return []
    const q = filter.trim().toLowerCase()
    if (!q) return data
    return data.filter(
      (t) =>
        (t.companyName ?? '').toLowerCase().includes(q) ||
        t.tenantId.toLowerCase().includes(q) ||
        (t.nip ?? '').toLowerCase().includes(q),
    )
  }, [data, filter])

  const onToggleBlocked = async (t: ManualTenantSummary) => {
    const label = t.companyName ?? t.tenantId
    const ok = await confirm(
      t.permanentlyBlocked
        ? {
            title: 'Odblokować firmę?',
            message: `O dostępie firmy „${label}" zdecyduje status faktur w panelu Claro dla NIP ${t.nip ?? '(brak NIP!)'}.`,
            confirmText: 'Odblokuj',
            cancelText: 'Anuluj',
          }
        : {
            title: 'Zablokować firmę?',
            message: `Firma „${label}" straci dostęp niezależnie od statusu faktur w panelu Claro.`,
            confirmText: 'Zablokuj',
            cancelText: 'Anuluj',
          },
    )
    if (ok) setBlocked.mutate({ tenantId: t.tenantId, blocked: !t.permanentlyBlocked })
  }

  if (isLoading) {
    return <div className="tw-p-6 tw-text-gray-500">Ładowanie firm…</div>
  }
  if (error) {
    const status = (error as any)?.response?.status
    const message =
      status === 403
        ? 'Brak uprawnień. Wymagane konto globalnego administratora.'
        : 'Nie udało się załadować listy firm.'
    return (
      <div className="tw-p-6 tw-text-red-600">
        {message}{' '}
        <button onClick={() => refetch()} className="tw-underline">
          Spróbuj ponownie
        </button>
      </div>
    )
  }

  return (
    <div className="tw-p-6">
      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-4">
        <div>
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">
            Rozliczenia ręczne
          </h3>
          <p className="tw-text-sm tw-text-gray-600">
            Firmy obsługiwane przez zewnętrzny system księgowy — faktury i status płatności
            ustawia administrator ręcznie. Płatności samoobsługowe (Przelewy24) nie zmieniają tu pól.
          </p>
        </div>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtruj po nazwie, ID, NIP…"
          className="tw-w-72 tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
        />
      </div>

      <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
        <table className="tw-w-full tw-text-sm">
          <thead className="tw-bg-gray-50 tw-text-gray-600 tw-uppercase tw-text-xs">
            <tr>
              <Th>Firma</Th>
              <Th>Pakiet</Th>
              <Th>Limit kierowców</Th>
              <Th>Tryb</Th>
              <Th>Wygasa</Th>
              <Th>Status</Th>
              <Th>Ostatnia płatność</Th>
              <Th className="tw-text-right">Akcja</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="tw-py-6 tw-text-center tw-text-gray-500">
                  Brak firm pasujących do filtra.
                </td>
              </tr>
            )}
            {rows.map((t) => {
              const status = computeStatus(t)
              const packageLabel = t.package != null
                ? `${t.package}${t.packageMultiplier > 1 ? ` ×${t.packageMultiplier}` : ''}`
                : '—'
              return (
                <tr key={t.tenantId} className="tw-border-t tw-border-gray-100 hover:tw-bg-gray-50">
                  <Td>
                    <div className="tw-font-medium tw-text-gray-900">{t.companyName ?? '—'}</div>
                    <div className="tw-text-xs tw-text-gray-500">
                      {t.nip ? `NIP: ${t.nip}` : 'brak NIP'}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-font-mono">{t.tenantId}</div>
                  </Td>
                  <Td>{packageLabel}</Td>
                  <Td>{t.activeDriversLimits ?? '—'}</Td>
                  <Td>{t.monthlySubscription ? 'mies.' : t.boundedPackage ? 'pakiet czasowy' : 'pakiet'}</Td>
                  <Td>{formatDate(t.subscriptionExpirationDate)}</Td>
                  <Td>
                    <span
                      className={`tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${toneClasses[status.tone]}`}
                    >
                      {status.label}
                    </span>
                  </Td>
                  <Td>{formatDate(t.paymentDate)}</Td>
                  <Td className="tw-text-right tw-whitespace-nowrap">
                    <button
                      onClick={() => onToggleBlocked(t)}
                      disabled={setBlocked.isPending}
                      title={
                        t.permanentlyBlocked
                          ? 'Zdejmij blokadę — o dostępie decyduje wtedy status faktur w panelu Claro'
                          : 'Zablokuj dostęp niezależnie od statusu w panelu Claro'
                      }
                      className={`tw-mr-2 tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer disabled:tw-opacity-50 ${
                        t.permanentlyBlocked
                          ? 'tw-bg-emerald-600 hover:tw-bg-emerald-700'
                          : 'tw-bg-red-600 hover:tw-bg-red-700'
                      }`}
                    >
                      {t.permanentlyBlocked ? 'Odblokuj' : 'Zablokuj'}
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/company/${t.tenantId}/administrationPanel/editCompany`)
                      }
                      className="tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-blue-600 hover:tw-bg-blue-700"
                    >
                      Edytuj
                    </button>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <th className={`tw-text-left tw-px-3 tw-py-2 tw-font-semibold ${className ?? ''}`}>{children}</th>
)
const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <td className={`tw-px-3 tw-py-2 tw-align-top ${className ?? ''}`}>{children}</td>
)
